import { useEffect, useState } from "react";
import { Dropdown, Pagination } from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";
import { API_URL } from "../../../services/apiConfig";
import axiosInstance from "../../../services/axios.instance";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ApiResponse } from "../../../types";
import { trackEvent } from "../../../services/events";
import ReactPixel from "react-facebook-pixel";
import { dashboard_vertical_three_dots } from "../../../assets/images";
// import ConfirmationModal from "../../ConfirmationModal";

const CustomerOrders = () => {
  const [currentPage, setCurrentPage] = useState(0);
  // const [showModal, setShowModal] = useState(false);
  // const [rejectConfirmationMessage, setRejectConfirmationMessage] =
  //   useState("");
  // const queryClient = useQueryClient();
  const navigate = useNavigate();
  const generatePageNumbers = (currentPage: number, totalPages: number) => {
    if (totalPages <= 7) {
      return Array.from({ length: totalPages }, (_, i) => i);
    }

    if (currentPage <= 3) {
      return [0, 1, 2, 3, -1, totalPages - 2, totalPages - 1];
    }

    if (currentPage >= totalPages - 4) {
      return [
        0,
        1,
        -1,
        totalPages - 4,
        totalPages - 3,
        totalPages - 2,
        totalPages - 1,
      ];
    }

    return [
      0,
      1,
      -1,
      currentPage - 1,
      currentPage,
      currentPage + 1,
      -1,
      totalPages - 1,
    ];
  };

  useEffect(() => {
    const trackPageLoad = async () => {
      if (!(window as any).fbq && process.env.REACT_APP_META_PIXEL_ID) {
        ReactPixel.init(process.env.REACT_APP_META_PIXEL_ID);
      }
      ReactPixel.track("ViewContent", {
        content_type: "page",
        content_name: "Order History Screen",
        content_category: "OrderHistory",
      });
      await trackEvent({
        event_name: { web: "Kirkos_YourOrderHistoryScreen", app: "Kirkos_YourOrderHistoryScreen_App" },
        event_category: "Order_Info",
        module: "OrderHistory",
        payload: {},
      });
    };
    trackPageLoad();
  }, []);
  const fetchCustomerOrders = async (page: number) => {
    const response = await axiosInstance.get(
      `${API_URL.CUSTOMER_ORDER}?page=${page}`
    );
    return response?.data;
  };

  const { data: approvedOrders } = useQuery<ApiResponse>({
    queryKey: ["approvedOrders", currentPage],
    queryFn: () => fetchCustomerOrders(currentPage),
  });

  const [visiblePages, setVisiblePages] = useState<number[]>(() => {
    const totalPages = approvedOrders?.totalPages || 0;
    return generatePageNumbers(0, totalPages);
  });

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    setVisiblePages(generatePageNumbers(page, approvedOrders?.totalPages || 0));
  };

  useEffect(() => {
    if (approvedOrders?.totalPages) {
      setVisiblePages(
        generatePageNumbers(currentPage, approvedOrders.totalPages)
      );
    }
  }, [approvedOrders, currentPage]);

  const formatDate = (isoDate: any) => {
    const date = new Date(isoDate);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    // return `${year}-${month}-${day}`;
    return `${day}-${month}-${year}`;
  };
  const [selectedOrderId, setSelectedOrderId] = useState<string>("");
  const [selectedOrderStatus, setSelectedOrderStatus] = useState<string>("");
  // const handleCancelClick = (orderId: string, orderStatus: string) => {
  //   setSelectedOrderId(orderId);
  //   setSelectedOrderStatus(orderStatus);
  //   setRejectConfirmationMessage(
  //     "Are you sure you want to cancel this order?\n\n" +
  //       " If you cancel within 48 hours of acceptance, you will receive a full refund.\n" +
  //       " If you cancel after 48 hours, cancellation charges will apply."
  //   );
  //   setShowModal(true);
  // };

  // const handleCancelOrder = async (orderId: string, orderStatus: string) => {
  //   try {
  //     const accessToken = localStorage.getItem("access_token");
  //     const response = await axiosInstance.put(
  //       `${API_URL.CUSTOMER_ORDER_CANCEL}`,
  //       {
  //         order_number: orderId,
  //         cancel_reason: "Customer request",
  //         order_status: orderStatus,
  //       },
  //       {
  //         headers: {
  //           "Content-Type": "application/json",
  //           Authorization: `Bearer ${accessToken}`,
  //           language: "english",
  //           location: "0,0",
  //         },
  //       }
  //     );

  //     if (response.status === 200) {
  //       await queryClient.invalidateQueries({ queryKey: ["approvedOrders"] });
  //     }
  //   } catch (error) {
  //     console.error("Error cancelling order:", error);
  //   }
  // };

  const getStatusClass = (status: string) => {
    if (status === "MERCHANT_ORDER_ACCEPT") return "status-accepted";
    if (status === "MERCHANT_ORDER_REJECTED") return "status-booked";
    if (status === "DELIVERED") return "status-delivered";
    if (status === "CANCELED") return "status-canceled";
    return "status-default";
  };

  return (
    <div className="row g-4">
      {/* Desktop View */}
      <div className="col-md-12 dashboard-table-div d-none d-md-block">
        <table className="table table-hover dashboard-table">
          <thead>
            <tr>
              <th>Order Number</th>
              <th>Merchant</th>
              <th>Status</th>
              <th>Product Name</th>
              <th>Purchase Date</th>
              <th>Action</th>
              <th>Extend</th>
            </tr>
          </thead>
          <tbody>
            {approvedOrders?.content && approvedOrders.content.length > 0 ? (
              approvedOrders?.content?.map((order: any, index: number) => (
                <tr key={index}>
                  <td>{order.orderId}</td>
                  <td>{order.merchantName}</td>
                  <td>
                    <span className={`status-badge ${getStatusClass(order.orderStatus)}`}>
                      {order.orderStatus === "MERCHANT_ORDER_ACCEPT"
                        ? "ORDER ACCEPTED"
                        : order.orderStatus === "MERCHANT_ORDER_REJECTED"
                          ? "BOOKED"
                          : order.orderStatus}
                    </span>
                  </td>
                  <td>{order.products?.[0]?.productName}</td>
                  <td>
                    <span className="invoice-date-td">
                      {formatDate(order.createdAt)}
                    </span>
                  </td>
                  <td>
                    <Dropdown>
                      <Dropdown.Toggle
                        variant="success"
                        id="dropdown-basic"
                        className="Dropdown-table"
                      >
                        <img
                          alt="options"
                          src={dashboard_vertical_three_dots}
                          width="15"
                          height="15"
                        />
                      </Dropdown.Toggle>
                      <Dropdown.Menu className="Dropdown-table-item">
                        <Dropdown.Item
                          as={Link}
                          to={`/customer/order/details?OrderId=${order?.orderId}`}
                          className="Dropdown-table-list Dropdown-table-item-link"
                        >
                          View Details
                        </Dropdown.Item>
                      </Dropdown.Menu>
                    </Dropdown>
                  </td>
                  <td>
                    {order?.orderStatus !== "RECOLLECTED" &&
                      order?.orderStatus !== "CANCELED" && (
                        <button
                          className="invoice-approve-btn"
                          onClick={() =>
                            navigate(`/customer/order/edit?OrderId=${order?.orderId}`, {
                              state: { buttonExtend: true }
                            })
                          }
                        >
                          Extend
                        </button>
                      )}
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={7}>
                  <div className="dashboard-empty-state">
                    <div className="empty-state-icon">
                      <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path><polyline points="14 2 14 8 20 8"></polyline><line x1="16" y1="13" x2="8" y2="13"></line><line x1="16" y1="17" x2="8" y2="17"></line><polyline points="10 9 9 9 8 9"></polyline></svg>
                    </div>
                    <h3 className="empty-state-title">No Orders Found</h3>
                    <p className="empty-state-text">You haven't placed any orders yet. Browse our collection to book your first car!</p>
                    <button className="empty-state-btn" onClick={() => navigate("/customer/myaccount")}>Browse Cars</button>
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Mobile View */}
      <div className="col-12 d-block d-md-none">
        {approvedOrders?.content && approvedOrders.content.length > 0 ? (
          approvedOrders?.content?.map((order: any, index: number) => (
            <div key={index} className="card customer-orders-mobile-card">
              <div className="mobile-card-header">
                <span className="mobile-order-id">Order ID: {order.orderId}</span>
                <span className={`status-badge ${getStatusClass(order.orderStatus)}`}>
                  {order.orderStatus === "MERCHANT_ORDER_ACCEPT" ? "ACCEPTED" : order.orderStatus === "MERCHANT_ORDER_REJECTED" ? "BOOKED" : order.orderStatus}
                </span>
              </div>
              <div className="mobile-card-body">
                <div className="mobile-info-grid">
                  <span className="mobile-info-label">Merchant</span>
                  <span className="mobile-info-value">{order.merchantName}</span>
                  
                  <span className="mobile-info-label">Product</span>
                  <span className="mobile-info-value">{order.products?.[0]?.productName}</span>
                  
                  <span className="mobile-info-label">Purchased</span>
                  <span className="mobile-info-value">{formatDate(order.createdAt)}</span>
                </div>
                
                <div className="mobile-card-actions">
                  <Link
                    to={`/customer/order/details?OrderId=${order?.orderId}`}
                    className="mobile-action-btn btn-view-details"
                  >
                    View Details
                  </Link>
                  {order?.orderStatus !== "RECOLLECTED" && order?.orderStatus !== "CANCELED" && (
                    <button
                      className="mobile-action-btn btn-extend"
                      onClick={() =>
                        navigate(`/customer/order/edit?OrderId=${order?.orderId}`, {
                          state: { buttonExtend: true },
                        })
                      }
                    >
                      Extend
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))
        ) : (
          <div className="dashboard-empty-state">
            <div className="empty-state-icon">
              <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path><polyline points="14 2 14 8 20 8"></polyline><line x1="16" y1="13" x2="8" y2="13"></line><line x1="16" y1="17" x2="8" y2="17"></line><polyline points="10 9 9 9 8 9"></polyline></svg>
            </div>
            <h3 className="empty-state-title">No Orders Found</h3>
            <button className="empty-state-btn mt-3" onClick={() => navigate("/customer/myaccount")}>Browse Cars</button>
          </div>
        )}
      </div>

      {/* Pagination */}
      {approvedOrders && approvedOrders.totalPages > 1 && (
        <div className="col-12 mt-4">
          <Pagination className="pagination-align">
            <Pagination.Prev
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 0}
            />
            {visiblePages.map((page, index) =>
              page === -1 ? (
                <Pagination.Ellipsis key={`ellipsis_${index}`} />
              ) : (
                <Pagination.Item
                  key={page}
                  active={page === currentPage}
                  onClick={() => handlePageChange(page)}
                >
                  {page + 1}
                </Pagination.Item>
              )
            )}
            <Pagination.Next
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === approvedOrders.totalPages - 1}
            />
          </Pagination>
        </div>
      )}
    </div>
  );
};

export default CustomerOrders;
